import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Download, Loader2, Package, RotateCcw, TrendingUp, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';

/**
 * Categorical palette.
 *
 * Fixed order rather than generated, so a carrier keeps the same colour between
 * the two charts on this page and between visits — a bar changing colour on
 * reload makes the comparison harder, not prettier.
 */
const SERIES_COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2'];

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function ReportsPage() {
  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 12);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  });

  const query = new URLSearchParams({ from: range.from, to: range.to }).toString();

  const { data, isLoading } = useQuery({
    queryKey: ['spend', range],
    queryFn: () => api.request(`/reports/spend?${query}`),
  });

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Reports</h1>
          <p className="text-slate-600">What the shop has spent on postage</p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="from" className="text-xs">
              From
            </Label>
            <Input
              id="from"
              type="date"
              value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="to" className="text-xs">
              To
            </Label>
            <Input
              id="to"
              type="date"
              value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="h-9"
            />
          </div>
          <Button variant="outline" asChild className="gap-2 h-9">
            <a href={`/api/reports/spend/export?${query}`} download>
              <Download className="w-4 h-4" />
              CSV
            </a>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              icon={Wallet}
              label="Total spend"
              value={currency.format(data.summary.totalSpend)}
            />
            <StatTile icon={Package} label="Parcels" value={data.summary.parcelCount.toLocaleString()} />
            <StatTile
              icon={TrendingUp}
              label="Average per parcel"
              value={currency.format(data.summary.averageCost)}
            />
            <StatTile
              icon={RotateCcw}
              label="Refunded"
              value={currency.format(data.summary.refundedValue)}
              caption={`${data.summary.refundedCount} label${data.summary.refundedCount === 1 ? '' : 's'}`}
            />
          </div>

          {data.summary.parcelCount === 0 ? (
            <Card className="border-none shadow-lg">
              <CardContent className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Nothing shipped in this period
                </h3>
                <p className="text-slate-500">Widen the dates, or ship something.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-none shadow-lg">
                <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle>Spend by month</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.byMonth} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        {/* Horizontal only: vertical lines add ink without helping
                            compare bar heights. */}
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="period" tickLine={false} axisLine={false} fontSize={12} />
                        <YAxis
                          tickFormatter={(value) => `$${value}`}
                          tickLine={false}
                          axisLine={false}
                          fontSize={12}
                          width={64}
                        />
                        <Tooltip
                          formatter={(value, name) =>
                            name === 'spend' ? [currency.format(value), 'Spend'] : [value, 'Parcels']
                          }
                          cursor={{ fill: '#f1f5f9' }}
                        />
                        <Bar dataKey="spend" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BreakdownCard title="By carrier" rows={data.byCarrier} />
                <BreakdownCard title="By service" rows={data.byService} />
                {/* Only worth a card once there is something to compare: a shop
                    that has only ever used one service, or shipped only parcels,
                    would see a single bar. */}
                {data.byProvider?.length > 1 && (
                  <BreakdownCard title="By shipping service (Shippo or EasyPost)" rows={data.byProvider} />
                )}
                {data.byKind?.length > 1 && (
                  <BreakdownCard title="Letters, flats and parcels" rows={data.byKind} />
                )}
              </div>

              {data.topOrders.length > 0 && (
                <Card className="border-none shadow-lg">
                  <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
                    <CardTitle>Biggest orders</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="px-6 py-3 font-medium">Order</th>
                          <th className="px-6 py-3 font-medium text-right">Parcels</th>
                          <th className="px-6 py-3 font-medium text-right">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topOrders.map((order) => (
                          <tr key={order.id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="px-6 py-3">
                              <Link
                                to={`/orders/${order.id}`}
                                className="font-medium text-blue-600 hover:underline"
                              >
                                {order.orderName}
                              </Link>
                              <span className="block text-xs text-slate-500">
                                {order.orderNumber}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-right tabular-nums">
                              {order.totalParcels}
                            </td>
                            <td className="px-6 py-3 text-right tabular-nums font-semibold">
                              {currency.format(order.totalCost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, caption }) {
  return (
    <Card className="border-none shadow-lg">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-slate-500 mb-2">
          <Icon className="w-4 h-4" />
          <span className="text-sm">{label}</span>
        </div>
        <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
        {caption && <p className="text-xs text-slate-500 mt-1">{caption}</p>}
      </CardContent>
    </Card>
  );
}

function BreakdownCard({ title, rows }) {
  const total = rows.reduce((sum, row) => sum + row.spend, 0);

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <ul className="space-y-3">
          {rows.map((row, index) => {
            const share = total > 0 ? (row.spend / total) * 100 : 0;
            const color = SERIES_COLORS[index % SERIES_COLORS.length];

            return (
              <li key={row.label}>
                <div className="flex items-baseline justify-between gap-4 mb-1">
                  <span className="font-medium text-slate-800 truncate">{row.label}</span>
                  <span className="tabular-nums text-slate-900 font-semibold shrink-0">
                    {currency.format(row.spend)}
                  </span>
                </div>
                {/* The bar carries the comparison; the numbers beside it carry
                    the detail. Both, because a share of spend is easier to see
                    and a dollar figure is easier to act on. */}
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${share}%`, backgroundColor: color }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {row.parcels} parcel{row.parcels === 1 ? '' : 's'} ·{' '}
                  {currency.format(row.averageCost)} each · {share.toFixed(0)}%
                </p>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
